import React from 'react';

import {
  QueryClient,
  QueryClientProvider as ReactQueryClientProvider,
} from '@tanstack/react-query';

import { showError } from '@components/toaster';
import { getScopedTranslate } from '@locale/translate';
import { seconds } from '@utils/milliseconds';

import { FieldError, NonFieldError } from './types';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: seconds(5),
      retry: false,
    },
  },
});

export const QueryClientProvider: React.FC = (props) => {
  return <ReactQueryClientProvider client={queryClient} {...props} />;
};

export const handlePossibleFieldError = (
  error: unknown,
  defaultMessage: string,
  callback?: () => void
) => {
  const typedError = error as FieldError;
  const { t } = getScopedTranslate(locale);
  if (typedError.response?.status === 403) {
    return handleError(t('noPermission'), callback);
  }
  const values = Object.values(typedError.response?.data ?? {});
  const flattenedValues = values.flat().filter(Boolean);
  const hasErrors = flattenedValues.length > 0;
  const message = hasErrors ? flattenedValues.join(' ') : defaultMessage;
  handleError(message, callback);
};

export const handleError = (error: unknown, callback?: () => void) => {
  if (typeof error === 'string') {
    showError(error);
  }
  if (typeof error === 'function') {
    error();
  }
  callback?.();
};

export const handlePossibleNonFieldError = (
  error: unknown,
  defaultMessage: string,
  callback?: () => void
) => {
  const typedError = error as NonFieldError;
  const { t } = getScopedTranslate(locale);
  if (typedError.response?.status === 403) {
    return handleError(t('noPermission'), callback);
  }
  const responseData = typedError.response?.data;
  const isArray = Array.isArray(responseData);
  const arrayMessages = isArray
    ? responseData.filter((item): item is string => typeof item === 'string')
    : [];
  const responseObject = !isArray ? responseData : undefined;
  const nonFieldMessages = responseObject?.nonFieldErrors
    ? responseObject.nonFieldErrors.filter((item): item is string => typeof item === 'string')
    : [];
  const message = arrayMessages.length
    ? arrayMessages.join(' ')
    : nonFieldMessages.length
    ? nonFieldMessages.join(' ')
    : defaultMessage;
  handleError(message, callback);
};

const locale = {
  en: {
    noPermission: 'You do not have permission to perform this action',
  },
};
